import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Bookmark, User } from 'lucide-react';
import { formatCount, timeAgo } from '@/lib/formatters';
import { formatBodyWithLinks } from '@/lib/postUtils';
import { processAvatarUrl } from '@/lib/mediaUtils';
import type { Post } from '@/types';

/** Get post content/body as formatted HTML (supports TipTap JSON). */
function getPostContentHtml(post: Post): string {
  const raw = post.content || (post as Record<string, unknown>).body as string || '';
  return formatBodyWithLinks(raw);
}

/** Get post author info. */
function getPostUser(post: Post): { name: string; avatar: string; username: string } {
  const u = post.user as Record<string, unknown> | undefined;
  const rawAvatar = (u?.avatarUrl as string) || (u?.avatar as string) || '';
  return {
    name: (u?.displayName as string) || (u?.name as string) || (u?.username as string) || 'Unknown',
    avatar: processAvatarUrl(rawAvatar) || '',
    username: (u?.username as string) || '',
  };
}

/** Get the first image URL from a post. */
function getPostImage(post: Post): string | undefined {
  const images = post.images as Array<Record<string, unknown>> | undefined;
  if (images && images.length > 0) {
    return (images[0]?.url as string) || (images[0] as unknown as string);
  }
  return post.videoUrls?.thumbnail;
}

/** Get all image URLs from a post. */
function getPostImages(post: Post): string[] {
  const images = post.images as Array<Record<string, unknown>> | undefined;
  if (!images) return [];
  return images.map((img) => (typeof img === 'string' ? img : (img?.url as string) || '')).filter(Boolean);
}

/** Get total likes count from engagement. */
function getLikesCount(post: Post): number {
  const counts = post.postEngagement?.reactionCounts;
  if (!counts) return 0;
  return Object.values(counts).reduce<number>((sum, n) => sum + ((n as number) || 0), 0);
}

export interface PostCardProps {
  post: Post;
  /** Override for liked state (for optimistic updates) */
  liked?: boolean;
  /** Override for like count (for optimistic updates) */
  likeCount?: number;
  /** Override for bookmarked state (for optimistic updates) */
  bookmarked?: boolean;
  /** Called when user clicks like button */
  onLike?: (post: Post) => void;
  /** Called when user clicks bookmark button */
  onBookmark?: (post: Post) => void;
  /** Called when user clicks comment button */
  onComment?: (postId: string) => void;
  /** Called when user clicks share button */
  onShare?: (postId: string) => void;
  /** Optional custom header action (e.g., follow button) - rendered before the menu button */
  headerAction?: React.ReactNode;
}

export default function PostCard({
  post,
  liked: likedOverride,
  likeCount: likeCountOverride,
  bookmarked: bookmarkedOverride,
  onLike,
  onBookmark,
  onComment,
  onShare,
  headerAction,
}: PostCardProps) {
  const navigate = useNavigate();
  const author = getPostUser(post);

  const liked = likedOverride ?? (post.postEngagement?.liked ?? post.userReaction === 'heart');
  const likeCount = likeCountOverride ?? getLikesCount(post);
  const bookmarked = bookmarkedOverride ?? post.postEngagement?.bookmarked ?? (post as Record<string, unknown>).isBookmarked ?? false;
  const images = getPostImages(post);
  const image = getPostImage(post);
  const contentHtml = getPostContentHtml(post);

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLike?.(post);
  };

  const handleBookmark = (e: React.MouseEvent) => {
    e.stopPropagation();
    onBookmark?.(post);
  };

  const handleComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    onComment?.(post.id);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(post.id);
  };

  return (
    <div className="bg-[#141821] rounded-2xl p-4 mb-3.5">
      {/* Author header */}
      <div className="flex items-center gap-3 mb-3">
        <div
          onClick={() => author.username && navigate(`/profile/${author.username}`)}
          className="cursor-pointer"
        >
          {author.avatar ? (
            <img src={author.avatar} alt={author.name} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#A38BFF]/20 flex items-center justify-center">
              <User size={18} className="text-[#A38BFF]" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <p
            onClick={() => author.username && navigate(`/profile/${author.username}`)}
            className="font-semibold text-sm text-[#F5F7FF] cursor-pointer hover:underline"
          >
            {author.name}
          </p>
          <p className="text-xs text-[#A7B0C8]">{post.createdAt ? timeAgo(post.createdAt) : ''}</p>
        </div>
        {headerAction}
        <button className="text-[#A7B0C8] hover:text-[#F5F7FF]">
          <span className="text-xl">{'\u22EF'}</span>
        </button>
      </div>

      {/* Content — tappable to open post detail */}
      <div onClick={() => navigate(`/post/${post.id}`)} className="cursor-pointer">
        {contentHtml && (
          <div
            className="text-sm text-[#F5F7FF] mb-3 post-content line-clamp-6"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        )}

        {/* Images */}
        {images.length > 1 ? (
          <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-3">
            {images.map((img, idx) => (
              <img key={idx} src={img} alt="" className="w-28 h-36 object-cover rounded-xl flex-shrink-0" />
            ))}
          </div>
        ) : image ? (
          <img src={image} alt="" className="max-w-sm max-h-64 w-auto rounded-xl mb-3" />
        ) : null}
      </div>

      {/* Engagement bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            aria-label={liked ? 'Unlike' : 'Like'}
            onClick={handleLike}
            className={`flex items-center gap-1.5 transition-colors ${liked ? 'text-[#A38BFF]' : 'text-[#A7B0C8]'}`}
          >
            <Heart size={20} fill={liked ? '#A38BFF' : 'none'} className="transition-transform active:scale-90" />
            <span className="text-sm">{formatCount(likeCount)}</span>
          </button>
          <button
            aria-label="Comment"
            onClick={handleComment}
            className="flex items-center gap-1.5 text-[#A7B0C8]"
          >
            <MessageCircle size={20} />
            <span className="text-sm">{formatCount(post.commentCount ?? 0)}</span>
          </button>
          <button
            aria-label="Share"
            onClick={handleShare}
            className="text-[#A7B0C8]"
          >
            <Share2 size={20} />
          </button>
        </div>
        <button
          aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark'}
          onClick={handleBookmark}
          className={`transition-colors ${bookmarked ? 'text-[#A38BFF]' : 'text-[#A7B0C8]'}`}
        >
          <Bookmark size={20} fill={bookmarked ? '#A38BFF' : 'none'} />
        </button>
      </div>
    </div>
  );
}

// Re-export utility functions for use elsewhere
export { getPostUser, getPostImage, getPostImages, getLikesCount, getPostContentHtml };
